import {
  GridColDef,
  GridColumnHeaders,
} from "@mui/x-data-grid-pro";
import { useTranslation } from "react-i18next";
import { LicenseCheckDisplayData } from "../models/LicenseCheckDisplayData";
import updateNextCheckDate from "../utils/updateNextCheckDate";
import { DateCell } from "./grid/cells/DateCell";
import { DateObjCell } from "./grid/cells/DateObjCell";
import { DurationCell } from "./grid/cells/DurationCell";
import { KeyCell } from "./grid/cells/KeyCell";
import { NameCell } from "./grid/cells/NameCell";
import { NumberCell } from "./grid/cells/NumberCell";
import { ResultCell } from "./grid/cells/ResultCell";
import { NoRowsOverlay } from "./grid/NoRowsOverlay";
import { I18nGrid } from "@/components/data/I18nGrid";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { ExportToolbar } from "@/components/grid/ExportToolbar";

interface Props {
  data?: LicenseCheckDisplayData[];
  detailedView: boolean;
  loading: boolean;
  /** Disables the export, e.g. while the driver list is incomplete. */
  exportDisabled?: boolean;
}

export function LicenseChecksGrid({ data, detailedView, loading, exportDisabled = false }: Props) {
  const { t } = useTranslation();
  const { geotabClient } = useGeotabClient();

  const columns: GridColDef[] = [
    {
      flex: 1,
      minWidth: 200,

      field: "firstName",
      headerName: t("licenseCheckAdmin.firstName", "First Name"),
      renderCell: NameCell,
    },
    {
      flex: 1,
      minWidth: 200,

      field: "lastName",
      headerName: t("licenseCheckAdmin.lastName", "Last Name"),
      renderCell: NameCell,
    },
    {
      flex: 1,
      minWidth: 350,

      field: "name",
      headerName: t("licenseCheckAdmin.email", "E-Mail"),
      renderCell: NameCell,
    },
    {
      flex: 1,
      maxWidth: 100,
      minWidth: 100,

      field: "userType",
      headerName: t("licenseCheckAdmin.userType", "User Type"),
      renderCell: NameCell,
    },
    {
      flex: 1,
      maxWidth: 180,
      minWidth: 180,

      field: "depositedKey",
      headerName: t("licenseCheckAdmin.depositedKey", "Deposited Key"),
      renderCell: KeyCell,
    },
    {
      flex: 1,
      minWidth: 180,

      field: "usedKey",
      headerName: t("licenseCheckAdmin.usedKey", "Used Key"),
      renderCell: KeyCell,
    },
    {
      flex: 1,
      minWidth: 180,

      field: "verificationDateTime",
      headerName: t("licenseCheckAdmin.verificationDateTime", "Verification Date"),
      renderCell: DateCell,
      valueGetter: value => (value === "---" ? null : value),
      type: "dateTime",
    },
    {
      flex: 1,
      minWidth: 180,

      field: "lastVerification",
      headerName: t("licenseCheckAdmin.lastVerification", "Last Verification"),
      renderCell: DateCell,
    },
    {
      flex: 1,
      minWidth: 180,

      field: "lastPositiveCheckDate",
      headerName: t("licenseCheckAdmin.lastPositiveCheckDate", "Last positive Verification"),
      renderCell: DateCell,
    },
    {
      flex: 1,
      minWidth: 250,

      field: "originalCheckDate",
      headerName: t("licenseCheckAdmin.originalCheckDate", "Original Checkdate"),
      renderCell: DateCell,
    },
    {
      flex: 1,
      minWidth: 180,
      type: "dateTime",
      field: "nextCheckDate",
      headerName: t("licenseCheckAdmin.nextVerification", "Next Verification"),
      renderCell: DateObjCell,
      editable: true,
    },
    {
      flex: 1,
      minWidth: 300,

      field: "daysSinceLastPositiveVerification",
      headerName: t(
        "licenseCheckAdmin.daysSinceLastPositiveVerification",
        "Days since last positive verification",
      ),
      renderCell: NumberCell,
    },

    {
      flex: 1,
      minWidth: 180,
      field: "timeOverdue",
      headerName: t("licenseCheckAdmin.timeOverdue", "Time Overdue"),
      renderCell: DurationCell,
    },

    {
      flex: 1,
      minWidth: 200,

      field: "statusMessage",
      headerName: t("licenseCheckAdmin.result", "Result"),
      renderCell: ResultCell,
    },
  ];

  return (
    <I18nGrid
      sx={{ flexGrow: 1 }}
      rows={data ?? []}
      getRowId={row => row.id}
      slots={{
        noRowsOverlay: NoRowsOverlay,
        columnHeaders: GridColumnHeaders,
        toolbar: ExportToolbar,
      }}
      slotProps={{
        loadingOverlay: {
          variant: "linear-progress",
          noRowsVariant: "skeleton",
        },
        toolbar: {
          report: { title: t("licenseCheckAdmin.title") },
          exportDisabled: loading || exportDisabled,
          hideQuickFilter: true,
        },
      }}
      settingsKey="licenseChecks"
      initialState={{
        sorting: {
          sortModel: [{ field: "lastVerification", sort: "desc" }],
        },
      }}
      columns={
        !detailedView
          ? columns.filter(col => col.field != "verificationDateTime")
          : columns.filter(col =>
              [
                "firstName",
                "lastName",
                "name",
                "userType",
                "depositedKey",
                "originalCheckDate",
                "timeOverdue",
                "verificationDateTime",
                "usedKey",
                "statusMessage",
              ].includes(col.field),
            )
      }
      processRowUpdate={async (updatedRow, _) => {
        await updateNextCheckDate(geotabClient, data, updatedRow);
        // refetchAddinData();
        return updatedRow;
      }}
      onProcessRowUpdateError={error => console.error(error)}
      loading={loading}
      checkboxSelection
    />
  );
}
